import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
    createTrackerConsents,
    getTrackerConsentsAsync,
    updateConsentsAsync,
} from "../../../helpers/trackers/createTrackerConsents";
import { deleteTrackerConsents } from "../../../helpers/trackers/deleteTrackerConsents";
import "./trackerSetting.css";
import Swal from "sweetalert2";
import SwalAlertComp from "../../../components/SwalAlert";
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    TextField,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
    KeywordInputRow,
    SectionBlock,
    SectionTitle,
    StyledKeywordInput,
} from "./GeneralSettings/GeneralSettings.styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";

const CookieConsent = () => {
    const language = useSelector((state) => state.languagesState.languages);
    const [inputData, setinputData] = useState("");
    const [consent, setConsent] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const params = useParams();
    const id = params.id;

    useEffect(() => {
        fetchData();
    }, []);

    let fetchData = async () => {
        let response = await getTrackerConsentsAsync(id);
        setConsent(response?.data?.consents);
        setinputData(response?.data?.consents?.name);
        setIsLoading(false);
    };

    const handleInputChange = (e) => {
        setinputData(e.target.value);
    };

    const handleCreateConsent = () => {
        const data = {
            tracker_id: params.id,
            name: inputData,
        };
        createConsents(data);
    };
    const createConsents = async (data) => {
        setIsSaving(true);
        try {
            let response = await createTrackerConsents(data);
            if (response.status) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.success ?? "Success",
                    message: response.message,
                    icon: "success",
                    timer: 3000,
                });
                setinputData("");
                fetchData();
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response?.message,
                    icon: "error",
                });
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleUpdateConsent = async () => {
        let data = {
            name: inputData,
        };
        let id = consent?.id;
        setIsUpdating(true);
        try {
            let response = await updateConsentsAsync(id, data);
            if (response.status) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.success ?? "Success",
                    message: response.message,
                    icon: "success",
                    timer: 3000,
                });
                setinputData("");
                fetchData();
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response?.message,
                    icon: "error",
                });
            }
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDeleteConsent = async () => {
        let data = {
            name: inputData,
        };
        let id = consent?.id;
        setIsDeleting(true);
        try {
            let response = await deleteTrackerConsents(id, data);
            if (response.status) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.success ?? "Success",
                    message: response.message,
                    icon: "success",
                    timer: 3000,
                });
                fetchData();
                setinputData("");
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response?.message,
                    icon: "error",
                });
            }
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <SectionBlock>
            {isLoading ? (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="140px"
                >
                    <CircularProgress color="primary" size={24} />
                </Box>
            ) : (
                <>
                    <SectionTitle>Cookie Consent</SectionTitle>
                    <Box sx={{ mb: 2 }}>
                        {language?.trackers_page?.use_this_page_to_specify ??
                            "Use this page to specify the name of the variable tracking your website's cookie consent. By providing the correct variable name, our system will automatically check the consent status and trigger the appropriate  events. If the specified cookie variable is set to 'true' or '1', it indicates consent has been given and events will be sent. If the variable is absent or set to 'false' or '0', no events will be triggered. Simply input the variable name and click 'save' to update."}
                    </Box>
                    <KeywordInputRow>
                        <StyledKeywordInput
                            size="small"
                            variant="outlined"
                            value={inputData}
                            onChange={handleInputChange}
                        />
                        {!consent && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleCreateConsent}
                                disabled={
                                    !inputData?.trim() ||
                                    isSaving ||
                                    isUpdating ||
                                    isDeleting
                                }
                                startIcon={
                                    isSaving ? (
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                        />
                                    ) : null
                                }
                                style={{ minWidth: "unset !important" }}
                            >
                                {isSaving
                                    ? "SAVING..."
                                    : (language?.buttons?.add ?? "Add")}
                            </Button>
                        )}
                        {consent && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleUpdateConsent}
                                disabled={
                                    !inputData?.trim() ||
                                    isUpdating ||
                                    isSaving ||
                                    isDeleting
                                }
                                startIcon={
                                    isUpdating ? (
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                        />
                                    ) : null
                                }
                                style={{ minWidth: "unset !important" }}
                            >
                                {isUpdating
                                    ? "UPDATING..."
                                    : (language?.buttons?.update ?? "UPDATE")}
                            </Button>
                        )}
                        {consent && (
                            <Button
                                variant="contained"
                                color="error"
                                onClick={handleDeleteConsent}
                                disabled={
                                    !inputData?.trim() ||
                                    isDeleting ||
                                    isSaving ||
                                    isUpdating
                                }
                                style={{ minWidth: "unset !important" }}
                            >
                                {isDeleting ? (
                                    <CircularProgress
                                        size={20}
                                        color="inherit"
                                    />
                                ) : (
                                    <FontAwesomeIcon icon={faTrashAlt} />
                                )}
                            </Button>
                        )}
                    </KeywordInputRow>
                </>
            )}
        </SectionBlock>
    );
};

export default CookieConsent;
